import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Users, GripVertical, Plus, ChevronUp, ChevronDown, UserX } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Kapasitetsstatus på treningskortet (før/etter «Jeg kommer»)
export const CapacityStatus = ({ event, compact = false }) => {
  const c = event?.capacity;
  if (!c) return null;
  const id = `${event.attendance_event_id}-${event.occurrence_date}`;
  if (c.my_team) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-300" data-testid={`capacity-my-team-${id}`}>
        <Users className="w-3.5 h-3.5 shrink-0" />Du er på Lag {c.my_team}
      </span>
    );
  }
  const full = c.open_team > 1;
  const text = full
    ? `Lag ${c.open_team - 1} fylt – meld deg på Lag ${c.open_team}`
    : `Lag 1: ${c.free_in_open_team} av ${c.team_size} plasser ledige`;
  return (
    <span className={`inline-flex items-center gap-1 ${compact ? 'text-[10px]' : 'text-xs'} ${full ? 'text-orange-300' : 'text-sky-300'}`}
      title={full ? `${c.free_in_open_team} av ${c.team_size} plasser ledige på Lag ${c.open_team}` : 'Lag fordeles etter hvem som melder seg på først'}
      data-testid={`capacity-status-${id}`}>
      <Users className="w-3.5 h-3.5 shrink-0" />{compact ? (full ? `Lag ${c.open_team}: ${c.free_in_open_team} ledige` : `${c.free_in_open_team}/${c.team_size} ledige`) : text}
    </span>
  );
};

const iconBtn = 'w-7 h-8 flex items-center justify-center rounded-md text-gray-400 hover:text-white hover:bg-gray-600 disabled:opacity-30 shrink-0 touch-manipulation';

const MemberChip = ({ m, idx, count, team, maxTeam, busy, onMove, onShift, onRemove, onDropOn, dragOver, setDragOver }) => {
  const [confirm, setConfirm] = useState(false);
  if (confirm) {
    return (
      <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-red-500/10 border border-red-500/30" data-testid={`team-remove-confirm-${m.user_id}`}>
        <span className="flex-1 min-w-0 text-sm text-red-200 truncate">Fjerne {m.user_name}?</span>
        <button type="button" disabled={busy} onClick={() => onRemove(m)} className="h-8 px-3 rounded-md bg-red-600 text-white text-xs font-semibold" data-testid={`team-remove-yes-${m.user_id}`}>Fjern</button>
        <button type="button" onClick={() => setConfirm(false)} className="h-8 px-3 rounded-md bg-gray-700 text-gray-200 text-xs" data-testid={`team-remove-no-${m.user_id}`}>Avbryt</button>
      </div>
    );
  }
  return (
    <div draggable onDragStart={(e) => { e.dataTransfer.setData('text/plain', m.user_id); e.stopPropagation(); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(m.user_id); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(null); onDropOn(e.dataTransfer.getData('text/plain'), team, idx); }}
      className={`flex items-center gap-1 p-1.5 rounded-lg bg-gray-700/60 cursor-grab active:cursor-grabbing ${dragOver === m.user_id ? 'ring-2 ring-yellow-400' : ''}`}
      data-testid={`team-member-${m.user_id}`}>
      <GripVertical className="w-4 h-4 text-gray-500 shrink-0 hidden sm:block" />
      <span className="w-5 text-center text-xs text-gray-400 shrink-0">{idx + 1}</span>
      <span className="flex-1 min-w-0 text-sm text-white truncate">{m.user_name}{m.via_qr && <span className="ml-1 text-[10px] text-sky-300">QR</span>}</span>
      <button type="button" className={iconBtn} disabled={busy || idx === 0} onClick={() => onShift(team, idx, -1)} aria-label="Flytt opp" data-testid={`team-up-${m.user_id}`}><ChevronUp className="w-4 h-4" /></button>
      <button type="button" className={iconBtn} disabled={busy || idx === count - 1} onClick={() => onShift(team, idx, 1)} aria-label="Flytt ned" data-testid={`team-down-${m.user_id}`}><ChevronDown className="w-4 h-4" /></button>
      <select value={team} disabled={busy} onChange={(e) => onMove(m.user_id, Number(e.target.value))}
        className="shrink-0 bg-gray-800 border border-gray-600 rounded-md text-xs text-gray-200 px-1 h-8 max-w-[76px]"
        aria-label="Flytt til lag" data-testid={`team-move-select-${m.user_id}`}>
        {Array.from({ length: maxTeam + 1 }, (_, i) => i + 1).map(t => (
          <option key={t} value={t}>{t > maxTeam ? `Nytt (${t})` : `Lag ${t}`}</option>
        ))}
      </select>
      <button type="button" className={`${iconBtn} hover:text-red-300`} disabled={busy} onClick={() => setConfirm(true)}
        aria-label="Fjern påmelding (møtte ikke)" title="Fjern påmelding (møtte ikke)" data-testid={`team-remove-${m.user_id}`}><UserX className="w-4 h-4" /></button>
    </div>
  );
};

// Lagoversikt for admin/skyteleder: flytt mellom lag, sorter innen lag, fjern påmelding
export const TeamsBoard = ({ event }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const params = { event_id: event.attendance_event_id, occurrence_date: event.occurrence_date };

  const load = useCallback(() => {
    axios.get(`${API_BASE}/api/calendar/attendance/teams`, { ...auth(), params })
      .then(r => setData(r.data)).catch(() => setData({ teams: [], total: 0 }));
    // eslint-disable-next-line
  }, [event.attendance_event_id, event.occurrence_date]);

  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); } catch (e) { toast.error(e.response?.data?.detail || 'Kunne ikke lagre'); }
    finally { setBusy(false); load(); }
  };
  const membersOf = (team) => (data.teams.find(t => t.team === team)?.members || []).map(m => m.user_id);
  const saveOrder = (team, ids) => axios.post(`${API_BASE}/api/calendar/attendance/reorder`, { ...params, team, user_ids: ids }, auth());

  const move = (user_id, team) => run(() => axios.post(`${API_BASE}/api/calendar/attendance/move`, { ...params, user_id, team }, auth()), `Flyttet til Lag ${team}`);
  const shift = (team, idx, dir) => {
    const ids = membersOf(team);
    [ids[idx], ids[idx + dir]] = [ids[idx + dir], ids[idx]];
    run(() => saveOrder(team, ids));
  };
  const remove = (m) => run(async () => {
    await axios.delete(`${API_BASE}/api/calendar/attendance/member`, { ...auth(), params: { ...params, user_id: m.user_id } });
    window.dispatchEvent(new Event('calendarRefresh'));
  }, `${m.user_name} er fjernet`);
  // Slipp på et medlem: flytt til laget (om nødvendig) og plasser foran det medlemmet
  const dropOn = (uid, team, idx) => {
    if (!uid) return;
    const fromTeam = data.teams.find(t => t.members.some(m => m.user_id === uid))?.team;
    run(async () => {
      if (fromTeam !== team) await axios.post(`${API_BASE}/api/calendar/attendance/move`, { ...params, user_id: uid, team }, auth());
      const ids = membersOf(team).filter(i => i !== uid);
      ids.splice(idx, 0, uid);
      await saveOrder(team, ids);
    });
  };

  if (!data) return <p className="text-gray-400 text-sm flex items-center gap-2 p-4"><Loader2 className="w-4 h-4 animate-spin" />Laster lag…</p>;
  const teams = data.teams.filter(t => t.team >= 1);
  const maxTeam = Math.max(0, ...teams.map(t => t.team));
  const size = data.team_size;
  const dropTeam = (team) => (e) => { e.preventDefault(); setOver(null); const uid = e.dataTransfer.getData('text/plain'); if (uid) dropOn(uid, team, membersOf(team).filter(i => i !== uid).length); };

  return (
    <div className="space-y-3" data-testid="teams-board">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
        <span>{data.location}</span>
        <span>{data.base_positions} standplasser{data.extra_spot ? ' + 1 ekstra' : ''} = <strong className="text-gray-200">{size} per lag</strong></span>
      </div>
      {teams.length === 0 && <p className="text-gray-400 text-sm" data-testid="teams-empty">Ingen har meldt seg på ennå.</p>}
      {teams.map(t => {
        const full = t.members.length >= size;
        return (
          <div key={t.team} onDragOver={(e) => { e.preventDefault(); setOver(t.team); }} onDragLeave={() => setOver(null)} onDrop={dropTeam(t.team)}
            className={`rounded-xl border p-2.5 space-y-1.5 transition-colors ${over === t.team ? 'border-yellow-400 bg-yellow-500/10' : 'border-gray-700 bg-gray-900/40'}`}
            data-testid={`team-${t.team}`}>
            <div className="flex items-center justify-between gap-2 px-1">
              <h4 className="text-white font-semibold">Lag {t.team}</h4>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${full ? 'bg-orange-500/20 text-orange-300' : 'bg-green-500/15 text-green-300'}`}
                data-testid={`team-count-${t.team}`}>{t.members.length} / {size}{full ? ' · fullt' : ''}</span>
            </div>
            {t.members.map((m, i) => (
              <MemberChip key={m.user_id} m={m} idx={i} count={t.members.length} team={t.team} maxTeam={maxTeam} busy={busy}
                onMove={move} onShift={shift} onRemove={remove} onDropOn={dropOn} dragOver={dragOver} setDragOver={setDragOver} />
            ))}
          </div>
        );
      })}
      {teams.length > 0 && (
        <div onDragOver={(e) => { e.preventDefault(); setOver('new'); }} onDragLeave={() => setOver(null)} onDrop={dropTeam(maxTeam + 1)}
          className={`hidden sm:flex items-center justify-center gap-2 rounded-xl border border-dashed p-3 text-sm ${over === 'new' ? 'border-yellow-400 text-yellow-300' : 'border-gray-600 text-gray-500'}`}
          data-testid="team-new-dropzone">
          <Plus className="w-4 h-4" />Slipp her for nytt lag ({maxTeam + 1})
        </div>
      )}
      <p className="text-sm text-gray-300 pt-1" data-testid="teams-total">Totalt: <strong className="text-white">{data.total}</strong> påmeldt{data.total === 1 ? '' : 'e'} på {teams.length} lag</p>
    </div>
  );
};
